/*
  Studio — selected-model capture + hero swap on the product-page fallback path.

  The homepage roster (snippets/model-roster.liquid) links a model card to its
  dedicated showroom page /models/<handle> when an ai_model metaobject exists.
  Until every metaobject is created, cards fall back to
  /products/ai-model-photo?model=<slug> so the link never 404s.

  The showroom page (sections/main-ai-model.liquid) captures the chosen model
  into the cart as properties[Model] + properties[Model handle]. The stock
  Horizon product page did NOT — so a customer arriving via the fallback lost
  their selection and saw the default product at checkout (Trello hIVJfKMC:
  "picking a model and proceeding to payment you see the default model").

  Card xy3VCDfZ hardens this fallback path with three behaviours, all gated on
  a valid ?model= that resolves against the server-emitted model map:
    A. Hero swap — promote the matching product media to the shown/first slide.
    B. Line-item property — inject properties[Model] (+ Model handle) so the
       selection rides the cart → checkout → order email → admin order detail.
    A(pill). A visible "Selected model: <Name>" note in the buy box.

  --- Why a server-emitted map (studio-selected-model-map) ---
  Shopify Liquid cannot read URL query params server-side, so this resolution is
  necessarily client-side. But snippets/selected-model.liquid publishes the
  AUTHORITATIVE slug → { name, id, index } map (same alt-text parse + shared
  snippets/model-slug.liquid formula as the roster). We look the URL slug up in
  that map to get the EXACT model display name — not a lossy reverse-of-the-slug
  guess — and the media id/index to promote to hero. When the map is absent
  (any non-ai-model-photo product) or the slug is not in it (bad/stale slug), we
  degrade gracefully: no property, no pill, no hero swap, generic gallery, no
  error. It stays a no-op on every other page, so loading it globally is safe.
*/
(function () {
  var params = new URLSearchParams(window.location.search || '');
  var slug = (params.get('model') || '').trim();
  if (!slug) return;

  // Reverse-slug fallback name, used only when the server map has no entry for
  // this slug (e.g. map failed to render). Lossy but better than nothing.
  function displayNameFromSlug(s) {
    var cleaned = s.replace(/-(women|men|boys|girls)$/i, '');
    cleaned = cleaned.replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
    if (!cleaned) cleaned = s.replace(/-/g, ' ').trim();
    return cleaned.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  // Resolve the slug against the server-emitted authoritative map.
  // Returns { name, id, index } or null.
  function resolveModel() {
    var mapEl = document.getElementById('studio-selected-model-map');
    if (!mapEl) return null;
    var list;
    try { list = JSON.parse(mapEl.textContent || '[]'); } catch (e) { return null; }
    if (!Array.isArray(list)) return null;
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].slug === slug) return list[i];
    }
    return null;
  }

  var resolved = resolveModel();
  // If a map exists but the slug is not in it, the selection is bad/stale:
  // do nothing at all (generic gallery, no property, no pill) per acceptance.
  if (document.getElementById('studio-selected-model-map') && !resolved) return;

  // Authoritative name when resolved; reverse-guess only if the map is missing.
  var modelName = (resolved && resolved.name) || displayNameFromSlug(slug);

  function ensureHidden(form, name, value) {
    var existing = form.querySelector('input[name="' + name + '"]');
    if (existing) {
      // On the fallback path the URL ?model= is the single source of truth, so
      // overwrite any pre-existing value rather than keep a stale/cached one.
      existing.value = value;
      return;
    }
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  // Y0fYDuh8 — publish the selection on the shared channel, ALWAYS. This is what
  // lets the composer hydrate itself instead of asking the customer to pick a
  // model a second time.
  function publishSelection() {
    var channel = window.StudioSelectedModel;
    if (!channel) return;          // channel script missing: fall back to writing directly
    channel.publish(modelName, slug, 'model-capture');
  }

  function wireForms() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    if (!forms.length) return false;

    // Y0fYDuh8 — ONE writer of properties[Model] / [Model handle]. When the
    // composer is on the page it owns both, hydrated from the selection this
    // script published above. Writing them here too was last-writer-wins: the
    // Model recorded on the order could differ from the one the customer chose,
    // and [Model handle] (written only here) could disagree with [Model]
    // (written only there) on the same line item. Silently.
    //
    // Once the composer has CLAIMED, this script simply stops writing. It does
    // not delete what it wrote before the claim: the composer ADOPTS existing
    // inputs by name (ensurePropInputs), so a pre-claim input becomes the
    // composer's input. An earlier revision withdrew them on claim instead,
    // which opened a window where a throw between the claim and the composer's
    // own install left the form with NO Model at all (Stage-1.5 B1, 2026-09-11).
    var channel = window.StudioSelectedModel;
    if (channel && channel.composerOwnsProperties()) return true;

    Array.prototype.forEach.call(forms, function (form) {
      // A form that renders its OWN Model inputs (the page-owned
      // `[data-model-prop]` hooks — sections/main-my-studio.liquid) is written by
      // that page's script. Writing here too made two writers on /pages/my-studio:
      // its form posts to /cart/add, so it matched the selector above, and a
      // customer's carousel pick was reverted to the ?model= value by the next
      // mutation (Stage-1.5 B3, reproduced in a real DOM).
      if (form.querySelector('[data-model-prop]')) return;
      ensureHidden(form, 'properties[Model]', modelName);
      ensureHidden(form, 'properties[Model handle]', slug);
    });
    return true;
  }

  function injectNote() {
    if (document.querySelector('[data-studio-model-note]')) return;
    var anchor =
      document.querySelector('.product-information') ||
      document.querySelector('.product-details') ||
      document.querySelector('main');
    if (!anchor) return;
    var note = document.createElement('p');
    note.setAttribute('data-studio-model-note', '');
    note.style.cssText =
      'margin:10px 0 0;font-size:14px;font-weight:600;' +
      'color:rgb(var(--color-foreground-rgb, 31 58 46));';
    note.textContent = '✓ Selected model: ' + modelName;
    var title = anchor.querySelector('h1');
    if (title && title.parentNode) {
      title.parentNode.insertBefore(note, title.nextSibling);
    } else {
      anchor.insertBefore(note, anchor.firstChild);
    }
  }

  /*
    Hero swap — promote the resolved model's media to the shown slide.

    We do NOT reorder the gallery DOM: Horizon's <media-gallery> / slideshow
    tracks an internal index and the zoom-dialog keys its slides by the original
    forloop index, so a DOM move would desync zoom + counter. Instead we drive
    the component's own selection API to make the matching slide the active one
    (Maya's photo becomes the shown hero), which is non-destructive and survives
    the component's lifecycle. Media are matched by data-media-id (rendered by
    snippets/product-media.liquid). Falls back to a scrollIntoView nudge if the
    slideshow API is unavailable. Runs at most once (guarded by a flag).
  */
  var swappedGalleryEl = null;
  function swapHero() {
    if (!resolved) return;
    var gallery = document.querySelector('media-gallery');
    if (!gallery || gallery === swappedGalleryEl) return;
    var targetId = String(resolved.id);
    var mediaEl = gallery.querySelector('[data-media-id="' + targetId + '"]');
    if (!mediaEl) return;

    // Prefer the component's own slideshow.select(index) — non-destructive.
    var slides = gallery.querySelectorAll('[data-media-id]');
    var idx = -1;
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].getAttribute('data-media-id') === targetId) { idx = i; break; }
    }
    var slideshow = gallery.slideshow;
    if (slideshow && typeof slideshow.select === 'function' && idx >= 0) {
      try {
        slideshow.select(idx, undefined, { animate: false });
        swappedGalleryEl = gallery;
        return;
      } catch (e) { /* fall through to scroll nudge */ }
    }

    // Fallback: bring the target media into view (grid presentation / no API).
    if (typeof mediaEl.scrollIntoView === 'function') {
      mediaEl.scrollIntoView({ block: 'start', behavior: 'auto' });
      swappedGalleryEl = gallery;
    }
  }

  // ---- pRo8vjsS — PDP wishlist heart wiring --------------------------------
  // The heart button is rendered hidden (style="display:none") by
  // snippets/product-media-gallery-content.liquid with an empty slug because
  // Liquid cannot read ?model= server-side. Once we have resolved slug + name,
  // fill in the attributes and make the button visible.
  // If the customer is logged in, check the server-emitted wishlist model list
  // (window.__wishlist_models, set below from the customer metafield) to
  // pre-fill the aria-pressed state without an extra fetch.
  function wireWishlistHeart() {
    var btn = document.querySelector('[data-wishlist-pdp-heart]');
    if (!btn || !slug) return;
    btn.setAttribute('data-model-slug', slug);
    btn.setAttribute('data-model-name', modelName);
    // Update aria-label with the resolved model name.
    var cfg = window.__wishlist || {};
    var savedLabel = (window.Shopify && window.Shopify.wishlistStrings && window.Shopify.wishlistStrings.save_label) || 'Save to wishlist';
    var removeLabel = (window.Shopify && window.Shopify.wishlistStrings && window.Shopify.wishlistStrings.remove_label) || 'Remove from wishlist';
    // Check server-pre-rendered saved state (injected by theme.liquid from the
    // customer metafield when loggedIn, so no extra fetch is needed).
    var savedSlugs = Array.isArray(window.__wishlist_saved_slugs) ? window.__wishlist_saved_slugs : [];
    var isSaved = savedSlugs.indexOf(slug) !== -1;
    btn.setAttribute('aria-pressed', String(isSaved));
    btn.setAttribute('aria-label', isSaved
      ? removeLabel.replace('%{name}', modelName).replace('{{ name }}', modelName)
      : savedLabel.replace('%{name}', modelName).replace('{{ name }}', modelName));
    btn.style.display = '';
  }

  function run() {
    // Y0fYDuh8 — publish first, and unconditionally: the selection is real
    // whether or not THIS page can carry it into a cart, and the composer
    // subscribes to it rather than asking the customer to choose again.
    // The channel refuses to let this downgrade a `composer` selection, so the
    // 8-second mutation window can no longer revert a deliberate re-pick.
    publishSelection();
    // Only act on pages that actually have an add-to-cart form. NOTE: this alone
    // does NOT keep us off /pages/my-studio — that page's form posts to /cart/add
    // too. What keeps us off it is the `[data-model-prop]` skip in wireForms().
    if (!wireForms()) return false;
    injectNote();
    swapHero();
    wireWishlistHeart();
    return true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Horizon can morph the buy-buttons / gallery blocks after first paint;
  // re-apply the hidden inputs, the visible note AND the hero selection on
  // mutations so the selection survives a section re-render. The setTimeout
  // below is the sole disconnect path: a per-callback "tries" counter would
  // trip within milliseconds on a busy page (lazy images, class toggles) and
  // disconnect before Horizon ever re-renders.
  if (window.MutationObserver) {
    var obs = new MutationObserver(function () {
      // A gallery re-render (variant update) replaces <media-gallery> with a new
      // node; swapHero()'s `gallery === swappedGalleryEl` guard sees the fresh
      // element differs from the one it last swapped and re-runs automatically —
      // no reset flag needed.
      run();
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(function () { obs.disconnect(); }, 8000);
  }
})();
